import React from 'react';
import { Container, Content, Col } from 'pages/new-questions/styles/common';
import { DecisionTreeQuestionProps } from './utils/types';
import { useDecisionTreeState } from './hooks/useDecisionTreeState';
import {
  QuestionHeader,
  MultiplePhaseView,
  SinglePhaseView,
  ResultsPhaseView,
  SubmitFooter,
} from './components';
import { useQuestionResolution } from '../../hooks/useQuestionResolution';
import { QuestionResolutionModals } from '../QuestionResolutionModals';

const DecisionTreeQuestion: React.FC<DecisionTreeQuestionProps> = ({
  question,
  questionNumber,
  totalQuestions,
  answeredQuestionsCount,
  userAnswersAnswered,
  resolved,
  setUserAnswersAnswered,
  isTest = false,
  handleSubmit,
  allowMultipleOnlyFlowWhenNoSingle = false,
  requireFinalSubmitClick = false,
  Header,
  BackButton,
}) => {
  const {
    questionTextShow,
    setQuestionTextShow,
    questionVideoStart,
    setQuestionVideoStart,
    modalVideoOption,
    setModalVideoOption,
    isLibrasVideo,
    setIsLibrasVideo,
    handlePlayVideo,
    handleReadResolution,
    formulaModalShow,
    setFormulaModalShow,
    handleShowFormula,
  } = useQuestionResolution({ hasLibrasVideo: !!question.video_embed_libras });

  const {
    state,
    currentStep,
    totalMultipleSteps,
    totalSingleSteps,
    handleMultipleOptionClick,
    handleSingleOptionClick,
    handleSingleSubmit,
    handleSelectCurrentStep,
  } = useDecisionTreeState({
    question,
    isTest,
    resolved,
    userAnswersAnswered,
    setUserAnswersAnswered,
    handleSubmit,
    allowMultipleOnlyFlowWhenNoSingle,
    requireFinalSubmitClick,
  });

  const currentReviewStep = state.userAnswers[state.reviewIndex];

  if (!currentStep && state.phase !== 'results') return null;

  const totalStepsCount = totalMultipleSteps + totalSingleSteps;

  const currentStepType =
    state.phase === 'multiple' ? 'multiple' : state.phase === 'single' ? 'single' : undefined;
  const currentStepIdx =
    state.phase === 'multiple' ? state.currentMultipleIndex : state.currentSingleIndex;

  return (
    <Container isTest={isTest}>
      <Content isTest={isTest}>
        {Header}
        <QuestionHeader
          questionNumber={questionNumber}
          totalQuestions={totalQuestions}
          answeredQuestionsCount={answeredQuestionsCount}
          caseCode={question?.code}
          showReviewBadges={resolved || state.finished}
          userAnswers={state.userAnswers}
          totalSteps={totalStepsCount}
          currentStepIndex={currentStepIdx}
          currentStepType={currentStepType}
          handleSelectCurrentStep={handleSelectCurrentStep}
        />
        <Col padding="20px 24px 24px" width="100%">
          {state.phase === 'results' && currentReviewStep && (
            <>
              <ResultsPhaseView question={question} currentReviewStep={currentReviewStep} />
            </>
          )}
          {state.phase === 'multiple' && currentStep && (
            <MultiplePhaseView
              question={question}
              currentStep={currentStep}
              selectedOption={state.selectedOption}
              onOptionClick={handleMultipleOptionClick}
              isReviewMode={resolved || state.finished}
            />
          )}
          {state.phase === 'single' && currentStep && (
            <SinglePhaseView
              question={question}
              currentStep={currentStep}
              currentIndex={state.currentSingleIndex}
              totalSteps={totalSingleSteps}
              selectedOption={state.selectedOption}
              onOptionClick={handleSingleOptionClick}
              isReviewMode={resolved || state.finished}
            />
          )}
        </Col>
        {!isTest && (
          <SubmitFooter
            currentIndex={
              state.phase === 'multiple' ? state.currentMultipleIndex : state.currentSingleIndex
            }
            totalSteps={state.phase === 'multiple' ? totalMultipleSteps : totalSingleSteps}
            selectedOption={state.selectedOption}
            onSubmit={handleSingleSubmit}
            BackButton={BackButton}
            phase={state.phase}
            finished={state.finished}
            waitingFinalSubmit={state.waitingFinalSubmit}
            hasCommentedQuestion={!!question.questao_comentada}
            hasVideoEmbed={!!question.video_embed}
            hasFormula={!!question.formula || !!question.formula_imagem}
            onReadResolution={handleReadResolution}
            onWatchResolution={handlePlayVideo}
            onShowFormula={handleShowFormula}
          />
        )}
        <QuestionResolutionModals
          modalVideoOption={modalVideoOption}
          setModalVideoOption={setModalVideoOption}
          setIsLibrasVideo={setIsLibrasVideo}
          questionVideoStart={questionVideoStart}
          setQuestionVideoStart={setQuestionVideoStart}
          isLibrasVideo={isLibrasVideo}
          videoEmbed={question?.video_embed}
          videoEmbedLibras={question?.video_embed_libras}
          questionTextShow={questionTextShow}
          setQuestionTextShow={setQuestionTextShow}
          questaoComentada={question?.questao_comentada}
          formulaModalShow={formulaModalShow}
          setFormulaModalShow={setFormulaModalShow}
          formula={question?.formula}
          formulaImagem={question?.formula_imagem}
        />
      </Content>
    </Container>
  );
};

export default DecisionTreeQuestion;
